import React from "react";
import { POS_COLORS } from "../../data/players";
import { BYE_WEEKS_2026 } from "../../data/scheduleData";
import { fillRosterSlots } from "../../utils/rosterSlotFill";
import { teamIsFull } from "../../utils/liveBoard";
import { isTvMode, typeScale, TV_ROW_PITCH } from "./BoardScale";
import theme from "../../data/theme";

/**
 * RosterBoard — every team, every slot. THE page, not a panel on it.
 *
 * This is the All Rosters view brought onto the page: a card per team with the
 * full slot ladder, each row a player and price or a bare slot chip, `$budget · Max $n`
 * under the name, the user's own seat highlighted. That design was already
 * right; it was just trapped in a modal.
 *
 * What changed bringing it out:
 *   - No modal chrome. No title, no ✕, no "Close & Resume".
 *   - 🔴 NO ARCHETYPE LINE. The modal prints team.profile.archetype — "WR
 *     Heavy", "Balanced", "Stars & Scrubs". Those are AI archetypes and these
 *     seats are real people, so the line is absent and never fabricated.
 *   - No PlayerCardContext. The modal calls usePlayerCard() directly, which
 *     destructures a null context and THROWS outside PlayerCardProvider — and
 *     the read-only viewer renders outside it, exactly like /scout does. The
 *     click is an optional prop instead, absent for spectators.
 *
 * Shared by the live board and the read-only share view, so a spectator sees
 * precisely what the person entering sees.
 */
const stripSuffix = (name) => name.replace(/\s+(Jr\.?|Sr\.?|II|III|IV|V)$/i, "").trim();

function toInitialLastName(name) {
  const clean = stripSuffix(name);
  const parts = clean.split(" ");
  if (parts.length < 2) return clean;
  return `${parts[0][0]}. ${parts.slice(1).join(" ")}`;
}

/**
 * Columns. The modal used ceil(teamCount / 2) — 6 across for 12 teams, 5 for
 * 10, which is two clean rows in both cases and is NOT the awkward 5/5/2 the
 * chunk 4 spec describes. Kept, with a cap so a 16-team league does not
 * produce eight 160px columns on a laptop.
 *
 * 🔴 SCALE TAKES COLUMNS AWAY, AND THAT IS THE REAL WORK OF THE TV CONTROL.
 * Bumping type inside a grid that still fits every team across just squeezes
 * each card until it is nothing but ellipses — bigger text in a narrower box is
 * not more readable. Twice the type wants roughly half the columns, so the base
 * count is divided by the scale and floored at 2: below that it stops being a
 * board and becomes a list.
 *
 * 🔴 AND THE RULE INVERTS AT TV. The division above exists because the page is
 * capped at 1500px — the only way to fit bigger text is a wider card. TV mode
 * removed that cap, so on a 2560px screen the same division works backwards:
 * three columns makes each card ~850px, and the big dead gap between a player's
 * name and his price is a symptom of that width rather than a row-layout
 * problem. At the base count the cards land near 500px and the gap closes on
 * its own. Ten teams is then a clean 5/5.
 *
 * At scale 1 this is byte-for-byte the old behaviour, which matters because two
 * mock-draft modals share this grid's CSS.
 */
export function columnsFor(teamCount, scale = 1) {
  const base = Math.min(6, Math.max(3, Math.ceil(teamCount / 2)));
  if (isTvMode(scale)) return base;
  const s = Number.isFinite(scale) && scale > 0 ? scale : 1;
  return Math.max(2, Math.min(base, Math.round(base / s)));
}

/**
 * 🔴 THE FALLBACK IS CSS, NOT A SECOND LAYOUT. `fullNames` swaps "C. McCaffrey"
 * for "Christian McCaffrey" at TV scale, where the same control has already
 * taken columns away and the cards are far wider. Nothing MEASURES a cell —
 * 12 teams x 16 slots is 192 measurements to make a per-row decision — so a
 * name that genuinely will not fit degrades the way it always has, on the
 * nowrap/overflow/ellipsis the row already carries at every scale.
 *
 * Suffixes stay stripped in both forms: "full first and last" is Kyle Pitts,
 * not Kyle Pitts Sr., and the suffix is the least useful thing in the cell.
 */
/**
 * 🔴 THE GLOW TABLE. Every mark on a card comes from here, keyed by the SAME
 * string that goes into `data-focus` — so the attribute cannot be right while
 * the glow is wrong, which is the defect this replaces. As shipped in ce99008
 * the attribute was `isNext ? "on-deck" : ""` while the border and shadow had
 * two weights keyed on a different variable, so every test passed in both
 * states and the distinction the grid was making had no handle at all.
 *
 * 🔴 NO SECOND KEYFRAME. `nomGlowPulse` (auction.css:51) is taken by name, at a
 * slower beat than NominationBlock's 1.9s: the banner is the loud element and a
 * roster card matching it turns the screen into two competing beacons. The
 * static `shadow` is what a reduced-motion or non-animating client still gets.
 *
 * 🔴 SOLD IS GREEN AND DOES NOT PULSE, deliberately. The pulse is the WAITING
 * verb — an unanswered question. A sale is a result, and the banner already
 * says so in green without animating; an orange pulse on the winner's card
 * while the banner is a static green panel is two surfaces disagreeing about
 * what just happened. One verb (glow), one keyframe, colour carries the state.
 */
const CARD_GLOW = {
  "up-now": {
    border: "rgba(255,149,0,0.55)",
    shadow: "0 0 0 1px rgba(255,149,0,0.35), 0 0 18px rgba(255,149,0,0.28)",
    animation: "nomGlowPulse 2.6s ease-in-out infinite",
    label: "Up now. This seat nominates next",
  },
  "on-deck": {
    border: "rgba(255,149,0,0.24)",
    shadow: "0 0 0 1px rgba(255,149,0,0.18), 0 0 12px rgba(255,149,0,0.16)",
    animation: undefined,
    label: "On deck",
  },
  sold: {
    border: "rgba(34,197,94,0.55)",
    shadow: "0 0 0 1px rgba(34,197,94,0.35), 0 0 22px rgba(34,197,94,0.30)",
    animation: undefined,
    label: "Just won a player",
  },
};

/**
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 A FINISHED TEAM LOOKS THE WAY IT DOES IN THE MOCK DRAFT. Ben: "I want the
 * DONE to look like it does in the mock draft part of the app."
 *
 * The answer was already written, in TeamGrid.jsx under `isRosteredOut`, and
 * these three values are lifted from it rather than reinvented: a red card
 * tint, the team name struck through, and the budget line REPLACED by ROSTER
 * FULL. Two surfaces of one app saying the same thing two different ways is
 * the thing worth fixing here; matching an existing answer is the fix.
 *
 * 🔴 IT REPLACES THE BUDGET LINE, IT DOES NOT ADD A ROW. That is what TeamGrid
 * does and it is why the treatment is loud without costing height — the same
 * "nothing marking a card may occupy layout space" rule this file's alignment
 * defect is recorded under. A finished team's `$0 · Max $0` says nothing
 * anybody needs; ROSTER FULL says the only thing that does.
 *
 * ⚠️ THIS REPLACED CHUNK F's TREATMENT — a 0.55 dim, an inset slate band and a
 * corner DONE pill, all deleted. Ben has seen both and chosen this one; layered
 * they would give a struck-through dimmed red banded card, which is noise
 * rather than emphasis.
 * ══════════════════════════════════════════════════════════════════════
 */
const DONE_BG = "rgba(239,68,68,0.10)";        // TeamGrid.jsx:39
const DONE_RED = "#ef4444";                    // TeamGrid.jsx:53

/**
 * How far an unfilled slot recedes.
 *
 * The row still has to read as a slot — it is the shape of the roster, and a
 * board that only draws the filled rows stops telling you how much is left —
 * but at 0.4 an empty row sat close enough to a filled one that a card scanned
 * as texture rather than as content. Pushing it back widens the gap between
 * "somebody is here" and "nobody is" without touching a single pixel of pitch.
 */
const EMPTY_ROW_OPACITY = 0.28;

const NO_FOCUS = { mode: "waiting", brightTeam: -1, faintTeam: -1, sale: null };

export default function RosterBoard({
  teams, rosterSlots, onOpenPlayer, onEditSale, sales,
  focus = NO_FOCUS, scale = 1, fullNames = false, board = null,
}) {
  const columns = columnsFor(teams.length, scale);
  // ONE FACTOR, every size derived from it. The alternative — bumping each
  // literal by hand — is how half of them get missed, and the ones that get
  // missed are the ones nobody was looking at when they tested it.
  //
  // typeScale adds the TV boost on top of the preset; below TV it is the scale
  // unchanged, so Normal and Large render exactly as they always have.
  const type = typeScale(scale);
  const s = (n) => Math.round(n * type);
  /**
   * 🔴 PITCH IS DECOUPLED FROM TYPE AT TV — the vertical lever, and the reason
   * sixteen slots fit a 1080p cast at all. Below TV every value here is
   * `undefined`, so the property is not emitted and Normal/Large render exactly
   * as they always have. See TV_ROW_PITCH.
   */
  const tight = isTvMode(scale);
  const rowPadY = tight ? TV_ROW_PITCH.padY : s(2);
  const rowGap = tight ? TV_ROW_PITCH.gap : s(2);
  const nameLineHeight = tight ? TV_ROW_PITCH.lineHeight : undefined;

  /**
   * WHICH SEAT IS LIT — decided in `boardFocus`, not here.
   *
   * ══════════════════════════════════════════════════════════════════════
   * 🔴 THIS REVERSES A RULE THIS FILE USED TO ARGUE FOR, AND THE REVERSAL IS
   * DELIBERATE. The old rule was "the banner and this grid must not name the
   * same person": NominationBlock already headlines `nominatorIndex` ("PJ'S
   * NOMINATION"), so the grid marked the seat AFTER it, to avoid printing one
   * fact twice.
   *
   * That is right for a laptop and wrong for the screen this exists for. THE
   * BANNER ANSWERS *WHO*; THE GRID ANSWERS *WHERE TO LOOK IN A 5×5* — different
   * questions, and across a room the banner's text may be the part you cannot
   * read. Duplicating a NAME is noise; a name plus a location is not.
   *
   * So waiting now lights the seat the room is waiting ON, brightly, and the
   * seat after it stays marked faintly as on deck. Do not "fix" this back
   * toward the old comment: it was reasoned, it shipped, and it was reversed on
   * purpose (Aug 12 2026) after Ben ran the board on a cast TV.
   * ══════════════════════════════════════════════════════════════════════
   *
   * The `nominatorIndex < 0` guard, the wrap and the precedence all live in
   * boardFocus. Nothing about which seat is lit is computed in this file.
   */
  const focusOf = (seat) => {
    if (seat === focus.brightTeam) return focus.mode === "sold" ? "sold" : "up-now";
    if (seat === focus.faintTeam) return "on-deck";
    return "";
  };

  return (
    <div
      className="view-all-rosters-grid live-roster-grid"
      data-testid="roster-grid"
      style={{
        "--cols": columns,
        // The mobile rule for player names is `!important` px, so the scaled
        // value has to reach it through a variable or a phone silently ignores
        // the control. Default in the CSS keeps the other two consumers of this
        // grid exactly as they were.
        "--vr-name": `${s(10)}px`,
        gap: s(8), alignItems: "start",
      }}
    >
      {teams.map((team, i) => {
        const isUser = !!team.isUser;
        const seat = team.teamIndex ?? i;
        // 🔴 ONE STRING DRIVES THE MARK, THE ATTRIBUTE AND THE SCREEN READER.
        // "" | "up-now" | "on-deck" | "sold" — the glow weight, `data-focus`
        // and the hidden label are all read out of CARD_GLOW by this key, so
        // they cannot disagree with each other.
        //
        // 🔴 AND IT COSTS NO LAYOUT. This used to be a small text badge in an
        // in-flow row above the team name, which made the marked card taller
        // than its neighbours and knocked every name in its row out of
        // alignment — a defect that shipped, because a mark and a misalignment
        // read as one intentional thing until somebody scans the row. A glow
        // paints outside the box model, so the mark can never do that again.
        // Nothing marking a card may occupy layout space; the hidden label and
        // the correction control below are absolutely positioned for the same
        // reason, and both sit LAST so the card's text still STARTS with the
        // team name — which is how the alignment rule is asserted.
        //
        // Still ADVISORY — real auctions skip full teams and drift, so this is
        // a prediction, one click to correct, gating nothing. ON DECK is a
        // prediction OF a prediction, so it is rendered quieter again: a
        // confidently wrong on-deck is the same failure one step further out.
        const mark = focusOf(seat);
        const glow = CARD_GLOW[mark] || null;
        const filled = fillRosterSlots(team.roster, rosterSlots);
        const emptySlots = rosterSlots.length - team.roster.length;
        /**
         * 🔴 FULLNESS COMES OFF `boardTotals`, NOT OFF `emptySlots`. There are
         * four disagreeing roster-size derivations feeding this screen
         * (buildLiveTeams, boardTotals, this file's rosterSlots.length,
         * useLiveBoard.rosterSizeOf) and they differ at the FALLBACK — so the
         * DONE badge and the entry row's "no roster spots left" warning could
         * disagree about the same card. Anchoring on one of them is the fix
         * that scales; unifying all four is post-draft work.
         *
         * `board` is optional because this component is also rendered from
         * hand-built `teams` in tests; without it the local count is the only
         * thing there is, and it is still right whenever rosterSize is set.
         */
        const full = board ? teamIsFull(board, seat) : emptySlots <= 0;
        /**
         * 🔴 A FINISHED TEAM CAN BID $0, NOT ITS WHOLE LEFTOVER BUDGET. This
         * was `emptySlots <= 1 ? team.budget : …`, which prints exactly that —
         * on the operator's cards and on /watch, where max bid is the one
         * derived number a spectator is deliberately given (see rule 3 in the
         * WatchPage header). buildLiveTeams (liveBoard.js) carried the same
         * collapse and feeds these same cards; both were fixed together.
         */
        const maxBid = full ? 0
          : emptySlots <= 1 ? team.budget
            : team.budget - (emptySlots - 1);
        // The slot that just filled, in the SOLD state. A card says WHERE to
        // look from ten feet; the row answers WHICH SLOT, which is the part Ben
        // asked for alongside "maybe the whole team".
        const soldName = mark === "sold" ? focus.sale?.name || null : null;

        return (
          <div
            key={i}
            data-testid={`team-card-${seat}`}
            // 🔴 A HANDLE THAT IS NOT A COLOUR. A glow cannot be asserted and a
            // class name is an implementation detail; this is what the tests
            // read, and it carries the MODE rather than mere presence so the
            // "exactly one bright element" invariant is assertable at all.
            data-focus={mark}
            style={{
              position: "relative",
              /**
               * 🔴 THE TINT IS STATE; THE GLOW IS EMPHASIS, AND THEY DO NOT
               * COMPETE. A finished seat stays red even when focus marks it —
               * Ben can name one deliberately from the board menu — but the
               * border and the box-shadow below are the baton's, unconditionally.
               * One card can say both "this seat is done" and "look here".
               */
              background: full ? DONE_BG : theme.colors.bg.surface,
              border: `1px solid ${glow ? glow.border
                : isUser ? theme.colors.border.brand : theme.colors.border.subtle}`,
              // 🔴 THE MARK IS A GLOW, AND A GLOW COSTS NO LAYOUT. box-shadow
              // paints outside the box model, so a marked card is exactly the
              // same size as an unmarked one — which is the whole fix. The
              // badge this replaces was an in-flow row above the team name, so
              // every name in the marked card's row stopped aligning.
              boxShadow: glow ? glow.shadow : undefined,
              animation: glow ? glow.animation : undefined,
              borderRadius: 8, padding: `${s(8)}px ${s(10)}px`,
              display: "flex", flexDirection: "column", gap: s(6),
            }}
          >
            {/* The content wrapper holds the card's flex column, so the header
                and the slot ladder keep the gap they always had. */}
            <div
              data-testid={`team-body-${seat}`}
              style={{ display: "flex", flexDirection: "column", gap: s(6) }}
            >
              <div>
                <div style={{
                  fontSize: s(12), fontWeight: 700,
                  color: isUser ? theme.colors.brand.orange : theme.colors.text.primary,
                  // TeamGrid.jsx:46 — the mock draft's own answer, matched.
                  textDecoration: full ? "line-through" : "none",
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>
                  {team.name || `Team ${i + 1}`}{isUser ? " (You)" : ""}
                </div>
                {/* The archetype line that used to sit here is deliberately gone. */}
                {/* 🔴 ONE LINE OR THE OTHER, NEVER BOTH — see the DONE note at
                    the top of the file. Same fontSize and the same marginTop as
                    the line it replaces, both through `s()`, so a finished card
                    is exactly the height of an unfinished one and the row it
                    sits in stays aligned. TeamGrid.jsx:53 is the source. */}
                {full ? (
                  <div
                    data-testid={`team-done-${seat}`}
                    style={{
                      fontSize: s(10), marginTop: 2, color: DONE_RED,
                      fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.5px",
                    }}
                  >Roster full</div>
                ) : (
                  <div style={{ fontSize: s(10), color: theme.colors.text.secondary, marginTop: 2 }}>
                    <span style={{ fontWeight: 700, color: team.budget < 20 ? theme.colors.status.danger : theme.colors.extended.emerald }}>${team.budget}</span>
                    {" · Max "}
                    <span style={{ fontWeight: 700, color: theme.colors.brand.accent }}>${maxBid}</span>
                  </div>
                )}
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: rowGap }}>
                {rosterSlots.map(slot => {
                  const pick = filled[slot.id];
                  const slotPos = slot.pos === "BN" ? "BN"
                    : slot.pos === "FLEX" ? "FLEX"
                      : slot.pos === "SFLEX" ? "SFLEX"
                        : pick?.player.pos || slot.pos;
                  const saleIndex = pick && sales
                    ? sales.findIndex(s => s.team === team.teamIndex && s.name === pick.player.name)
                    : -1;
                  const clickable = pick && (onEditSale ? saleIndex >= 0 : !!onOpenPlayer);
                  // 🔴 A TINT, NOT A BORDER OR A PADDING BUMP. The row that just
                  // filled has to be findable without changing the pitch of the
                  // other fifteen — sixteen rows times two card rows is where a
                  // one-pixel mistake becomes a card that overflows a cast TV.
                  const justSold = !!soldName && pick?.player.name === soldName;

                  return (
                    <div
                      key={slot.id}
                      data-testid={`slot-row-${slot.label}`}
                      data-sold={justSold ? "1" : undefined}
                      style={{
                        display: "flex", alignItems: "center", gap: s(5),
                        padding: `${rowPadY}px 0`, opacity: pick ? 1 : EMPTY_ROW_OPACITY,
                        background: justSold ? "rgba(34,197,94,0.16)" : undefined,
                        borderRadius: justSold ? 4 : undefined,
                      }}
                    >
                      {/* Tightened with the row: once the line box is tight the
                          CHIP is the tallest thing here and would silently set
                          the pitch for all sixteen rows. */}
                      <span className="tag" style={{
                        background: POS_COLORS[slotPos]?.bg || theme.colors.bg.elevated,
                        color: POS_COLORS[slotPos]?.text || theme.colors.text.muted,
                        fontSize: s(8),
                        padding: `${tight ? TV_ROW_PITCH.tagPadY : s(1)}px ${s(4)}px`,
                        lineHeight: tight ? TV_ROW_PITCH.tagLineHeight : undefined,
                        minWidth: s(26),
                        textAlign: "center", fontWeight: 700,
                      }}>
                        {slot.label}
                      </span>
                      {pick ? (
                        <>
                          <span
                            className="vr-player-name"
                            onClick={clickable
                              ? () => (onEditSale ? onEditSale(saleIndex) : onOpenPlayer(pick.player.name))
                              : undefined}
                            style={{
                              flex: 1, fontSize: s(10), fontWeight: 700, color: theme.colors.text.primary,
                              lineHeight: nameLineHeight,
                              cursor: clickable ? "pointer" : "default",
                              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                            }}
                            title={onEditSale && clickable ? `${pick.player.name}. Click to correct or delete` : pick.player.name}
                          >
                            {fullNames ? stripSuffix(pick.player.name) : toInitialLastName(pick.player.name)}
                          </span>
                          {BYE_WEEKS_2026[pick.player.team] && (
                            <span style={{ fontSize: s(9), color: theme.colors.text.muted }} title="Bye week">
                              B{BYE_WEEKS_2026[pick.player.team]}
                            </span>
                          )}
                          <span style={{ fontSize: s(10), fontWeight: 700, color: theme.colors.brand.accent }}>${pick.price}</span>
                        </>
                      ) : (
                        /* 🔴 A SPACER, NOT AN EMPTY ELEMENT. The word is gone —
                           it was noise on ~140 of 160 rows for most of a draft —
                           but the span stays at the NAME's exact font size and
                           line box, because an element with no content
                           contributes no line box at all and the empty row
                           would collapse shorter than the filled one. Sixteen
                           slots times two card rows is where that becomes a
                           card that overflows a cast TV. The nbsp is what holds
                           the height; nothing about the pitch changed. */
                        <span aria-hidden="true" style={{ flex: 1, fontSize: s(10), lineHeight: nameLineHeight }}>{" "}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            {/* 🔴 THE MARK IS COLOUR-ONLY NOW, SO THE SEMANTICS COME BACK HERE.
                The badge this replaced was text a screen reader announced;
                a box-shadow announces nothing. Absolutely positioned and
                clipped, so it costs no layout — reopening that defect to fix
                accessibility would be trading one real bug for another — and
                LAST in the card, so the card's text still starts with the team
                name, which is how the alignment rule is asserted. */}
            {glow && (
              <span style={{
                position: "absolute", width: 1, height: 1, overflow: "hidden",
                clip: "rect(0 0 0 0)", clipPath: "inset(50%)", whiteSpace: "nowrap",
              }}>{glow.label}</span>
            )}
            {/* 🔴 NOTHING ELSE GOES IN THIS CARD. `skip ahead →` / `not them →`
                used to sit absolutely positioned in this corner; Ben asked for
                it gone and it went, along with the onAdvanceNominator prop.
                Correcting whose turn it is now lives in BoardMenu, where the
                whole team list is one click each — the per-card control only
                ever did `nominatorIndex + 1`, so fixing a drifted prediction on
                a ten-team board could take nine clicks with somebody waiting.
                Deleting the element is a stronger form of "nothing marking a
                card may occupy layout space" than positioning it out of flow. */}
          </div>
        );
      })}
    </div>
  );
}
